import Link from "next/link";
import { redirect } from "next/navigation";

import { auth } from "@/auth";
import { prisma } from "@/lib/prisma";

import DeleteProductButton from "./delete-product-button";

type SearchParams = Promise<{
  q?: string;
  status?: string;
  category?: string;
}>;

type AdminArticlesPageProps = {
  searchParams: SearchParams;
};

function formatPrice(price: number) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(price);
}

function getStatusLabel(isPublished: boolean) {
  return isPublished ? "Publié" : "Brouillon";
}

function getStatusClasses(isPublished: boolean) {
  return isPublished
    ? "border-[#f1bfd5] bg-[#fff4f9] text-[#b64678]"
    : "border-[#eadfd2] bg-[#fffaf4] text-[#9a6a3b]";
}

export default async function AdminArticlesPage({
  searchParams,
}: AdminArticlesPageProps) {
  const session = await auth();

  if (!session?.user) {
    redirect("/connexion?callbackUrl=/admin/articles");
  }

  if (session.user.role !== "ADMIN") {
    redirect("/compte");
  }

  const params = await searchParams;
  const query = params.q?.trim() ?? "";
  const status = params.status?.trim() ?? "all";
  const category = params.category?.trim() ?? "all";

  const where = {
    AND: [
      query
        ? {
            OR: [
              {
                name: {
                  contains: query,
                  mode: "insensitive" as const,
                },
              },
              {
                slug: {
                  contains: query,
                  mode: "insensitive" as const,
                },
              },
              {
                sku: {
                  contains: query,
                  mode: "insensitive" as const,
                },
              },
            ],
          }
        : {},
      status === "published"
        ? { isPublished: true }
        : status === "draft"
          ? { isPublished: false }
          : {},
      category !== "all" ? { category: { slug: category } } : {},
    ],
  };

  const [products, categories, totalProducts, publishedProducts, draftProducts] =
    await Promise.all([
      prisma.product.findMany({
        where,
        orderBy: {
          createdAt: "desc",
        },
        include: {
          category: true,
          images: {
            orderBy: {
              sortOrder: "asc",
            },
            take: 1,
          },
        },
      }),
      prisma.category.findMany({
        orderBy: {
          name: "asc",
        },
      }),
      prisma.product.count(),
      prisma.product.count({
        where: {
          isPublished: true,
        },
      }),
      prisma.product.count({
        where: {
          isPublished: false,
        },
      }),
    ]);

  return (
    <div className="space-y-3 md:space-y-5 xl:space-y-6">
      <section className="overflow-hidden rounded-[1.75rem] border border-[#f2d7e4] bg-[linear-gradient(135deg,#fff8fb_0%,#fffdfa_58%,#fdf4f7_100%)] shadow-[0_16px_48px_rgba(196,78,124,0.07)]">
        <div className="grid gap-4 px-4 py-4 sm:gap-5 sm:px-5 sm:py-5 lg:grid-cols-[minmax(0,1fr)_minmax(240px,300px)] lg:items-end xl:grid-cols-[minmax(0,1.1fr)_280px]">
          <div className="space-y-4">
            <span className="inline-flex w-fit items-center rounded-full border border-[#efc7d9] bg-white/85 px-2.5 py-1 font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.22em] text-[#b64678] backdrop-blur">
              Studio admin
            </span>

            <div className="space-y-2">
              <h1 className="max-w-[12ch] text-[2rem] font-semibold leading-[0.95] tracking-[-0.05em] text-[#24161d] sm:max-w-none sm:text-[2.35rem] lg:text-[2.8rem]">
                Gestion des articles
              </h1>
              <p className="max-w-[58ch] text-[13px] leading-6 text-[#6f5a63] sm:text-sm sm:leading-6">
                Un espace plus éditorial pour piloter la boutique, suivre les
                publications et garder une vision claire sur le catalogue Jour J.
              </p>
            </div>

            <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap">
              <Link
                href="/admin/articles/nouveau"
                className="inline-flex min-h-10 items-center justify-center rounded-full bg-[#d85c92] px-4 py-2 text-sm font-medium text-white shadow-[0_10px_24px_rgba(216,92,146,0.22)] transition hover:bg-[#c94b82]"
              >
                Ajouter un article
              </Link>
              <Link
                href="/boutique"
                className="inline-flex min-h-10 items-center justify-center rounded-full border border-[#efc7d9] bg-white/85 px-4 py-2 text-sm font-medium text-[#6f324e] transition hover:border-[#e3a9c5] hover:bg-white"
              >
                Voir la boutique
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2.5 lg:grid-cols-1">
            <div className="rounded-[1.35rem] border border-white/80 bg-white/80 p-3.5 shadow-[0_8px_22px_rgba(36,22,29,0.045)] backdrop-blur">
              <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.2em] text-[#a88495]">
                Total
              </p>
              <p className="mt-2 text-[1.75rem] font-semibold leading-none tracking-[-0.05em] text-[#24161d]">
                {totalProducts}
              </p>
            </div>
            <div className="rounded-[1.35rem] border border-white/80 bg-white/80 p-3.5 shadow-[0_8px_22px_rgba(36,22,29,0.045)] backdrop-blur">
              <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.2em] text-[#a88495]">
                Publiés
              </p>
              <p className="mt-2 text-[1.75rem] font-semibold leading-none tracking-[-0.05em] text-[#24161d]">
                {publishedProducts}
              </p>
            </div>
            <div className="rounded-[1.35rem] border border-white/80 bg-white/80 p-3.5 shadow-[0_8px_22px_rgba(36,22,29,0.045)] backdrop-blur">
              <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.2em] text-[#a88495]">
                Brouillons
              </p>
              <p className="mt-2 text-[1.75rem] font-semibold leading-none tracking-[-0.05em] text-[#24161d]">
                {draftProducts}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-[1.75rem] border border-[#f0dfe7] bg-[#fffdfb] p-4 shadow-[0_10px_28px_rgba(36,22,29,0.035)] sm:p-5">
        <div className="mb-4 flex flex-col gap-1.5">
          <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[11px] uppercase tracking-[0.22em] text-[#b38a9d]">
            Recherche & tri
          </p>
          <h2 className="text-xl font-semibold tracking-[-0.04em] text-[#24161d] sm:text-[1.7rem]">
            Filtrer le catalogue
          </h2>
        </div>

        <form className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,1.45fr)_200px_200px_auto] xl:items-end">
          <div className="space-y-2">
            <label htmlFor="q" className="text-sm font-medium text-[#6f5a63]">
              Rechercher
            </label>
            <input
              id="q"
              name="q"
              defaultValue={query}
              placeholder="Nom, slug ou SKU"
              className="h-10 w-full rounded-[1.1rem] border border-[#ead6df] bg-white px-3.5 text-sm text-[#24161d] outline-none transition placeholder:text-[#bda9b2] focus:border-[#d97aa5] focus:ring-4 focus:ring-[#f8dce9]"
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="status"
              className="text-sm font-medium text-[#6f5a63]"
            >
              Statut
            </label>
            <select
              id="status"
              name="status"
              defaultValue={status}
              className="h-10 w-full rounded-[1.1rem] border border-[#ead6df] bg-white px-3.5 text-sm text-[#24161d] outline-none transition focus:border-[#d97aa5] focus:ring-4 focus:ring-[#f8dce9]"
            >
              <option value="all">Tous</option>
              <option value="published">Publiés</option>
              <option value="draft">Brouillons</option>
            </select>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="category"
              className="text-sm font-medium text-[#6f5a63]"
            >
              Catégorie
            </label>
            <select
              id="category"
              name="category"
              defaultValue={category}
              className="h-10 w-full rounded-[1.1rem] border border-[#ead6df] bg-white px-3.5 text-sm text-[#24161d] outline-none transition focus:border-[#d97aa5] focus:ring-4 focus:ring-[#f8dce9]"
            >
              <option value="all">Toutes</option>
              {categories.map((item) => (
                <option key={item.id} value={item.slug}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap md:col-span-2 xl:col-span-1 xl:justify-end">
            <button
              type="submit"
              className="inline-flex min-h-10 items-center justify-center rounded-full bg-[#24161d] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#3a2430]"
            >
              Filtrer
            </button>
            <Link
              href="/admin/articles"
              className="inline-flex min-h-10 items-center justify-center rounded-full border border-[#efc7d9] bg-white px-4 py-2 text-sm font-medium text-[#6f324e] transition hover:border-[#e3a9c5] hover:bg-[#fff7fb]"
            >
              Réinitialiser
            </Link>
          </div>
        </form>
      </section>

      <section className="overflow-hidden rounded-[1.75rem] border border-[#f0dfe7] bg-[#fffdfb] shadow-[0_14px_36px_rgba(36,22,29,0.04)]">
        <div className="flex flex-col gap-2 border-b border-[#f1e3ea] px-4 py-3.5 sm:flex-row sm:items-end sm:justify-between sm:px-5">
          <div>
            <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[11px] uppercase tracking-[0.22em] text-[#b38a9d]">
              Catalogue
            </p>
            <h2 className="mt-1 text-xl font-semibold tracking-[-0.04em] text-[#24161d] sm:text-[1.7rem]">
              Articles
            </h2>
          </div>
          <p className="text-sm text-[#8d7781]">
            {products.length} résultat{products.length > 1 ? "s" : ""}
          </p>
        </div>

        {products.length === 0 ? (
          <div className="px-5 py-12 text-center sm:px-6 sm:py-14">
            <div className="mx-auto max-w-md rounded-[1.5rem] border border-dashed border-[#ebcfdd] bg-[#fff8fb] px-5 py-8">
              <h3 className="text-xl font-semibold text-[#24161d]">
                Aucun article trouvé
              </h3>
              <p className="mt-3 text-sm leading-7 text-[#6f5a63] sm:text-base">
                Essaie d&apos;ouvrir un peu les filtres ou crée un nouvel article
                pour enrichir la boutique.
              </p>
              <div className="pt-5">
                <Link
                  href="/admin/articles/nouveau"
                  className="inline-flex items-center justify-center rounded-full bg-[#d85c92] px-4 py-2.5 text-sm font-medium text-white shadow-[0_14px_34px_rgba(216,92,146,0.24)] transition hover:bg-[#c94b82]"
                >
                  Créer un article
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-[#f1e3ea]">
            {products.map((product) => {
              const coverImage = product.images[0]?.imageUrl ?? null;

              return (
                <article
                  key={product.id}
                  className="grid gap-3 px-4 py-4 transition duration-200 hover:bg-[#fff9fc] sm:px-5 lg:grid-cols-[minmax(0,1.1fr)_140px_130px_90px_120px] lg:items-start lg:gap-x-4 lg:gap-y-3 xl:grid-cols-[minmax(0,1.25fr)_150px_140px_100px_130px_auto] xl:items-center xl:gap-x-5"
                >
                  <div className="flex min-w-0 items-start gap-3 sm:gap-4 lg:row-span-2 lg:items-center">
                    <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-[1rem] border border-[#edd7e2] bg-[#faf4f7] sm:h-16 sm:w-16">
                      {coverImage ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={coverImage}
                          alt={product.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center px-2 text-center font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.16em] text-[#b79eaa]">
                          Sans image
                        </div>
                      )}
                    </div>

                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="line-clamp-2 text-[15px] font-semibold leading-5 tracking-[-0.03em] text-[#24161d] sm:text-base">
                          {product.name}
                        </h3>
                        {product.isFeatured ? (
                          <span className="inline-flex items-center rounded-full border border-[#f2cade] bg-[#fff3f8] px-2.5 py-1 font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.18em] text-[#bb4f80]">
                            Mise en avant
                          </span>
                        ) : null}
                      </div>

                      <p className="truncate text-[13px] text-[#8d7781]">
                        /{product.slug}
                      </p>

                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-[#a28a95] sm:text-xs">
                        {product.sku ? (
                          <span className="font-[family-name:var(--font-ibm-plex-mono)] uppercase tracking-[0.16em]">
                            SKU · {product.sku}
                          </span>
                        ) : null}
                        {product.isGlitter ? <span>Pailleté</span> : null}
                        {product.color ? <span>{product.color}</span> : null}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-0.5 rounded-[1rem] border border-[#f4e7ed] bg-[#fffafb] px-3 py-2.5 lg:min-h-[76px] lg:bg-transparent lg:px-0 lg:py-0 lg:text-left lg:border-0 xl:text-center">
                    <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.18em] text-[#b38a9d]">
                      Catégorie
                    </p>
                    <p className="text-sm font-medium text-[#4f3c45]">
                      {product.category.name}
                    </p>
                  </div>

                  <div className="space-y-0.5 rounded-[1rem] border border-[#f4e7ed] bg-[#fffafb] px-3 py-2.5 lg:min-h-[76px] lg:bg-transparent lg:px-0 lg:py-0 lg:text-left lg:border-0 xl:text-center">
                    <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.18em] text-[#b38a9d]">
                      Prix
                    </p>
                    <div className="space-y-1">
                      <p className="text-base font-semibold text-[#24161d]">
                        {formatPrice(Number(product.price))}
                      </p>
                      {product.compareAtPrice ? (
                        <p className="text-xs text-[#b09aa5] line-through">
                          {formatPrice(Number(product.compareAtPrice))}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="space-y-0.5 rounded-[1rem] border border-[#f4e7ed] bg-[#fffafb] px-3 py-2.5 lg:min-h-[76px] lg:bg-transparent lg:px-0 lg:py-0 lg:text-left lg:border-0 xl:text-center">
                    <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.18em] text-[#b38a9d]">
                      Stock
                    </p>
                    <p className="text-base font-semibold text-[#24161d]">
                      {product.stock}
                    </p>
                  </div>

                  <div className="space-y-0.5 rounded-[1rem] border border-[#f4e7ed] bg-[#fffafb] px-3 py-2.5 lg:min-h-[76px] lg:bg-transparent lg:px-0 lg:py-0 lg:text-left lg:border-0 xl:text-center">
                    <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.18em] text-[#b38a9d]">
                      Statut
                    </p>
                    <span
                      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${getStatusClasses(
                        product.isPublished,
                      )}`}
                    >
                      {getStatusLabel(product.isPublished)}
                    </span>
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap lg:col-span-full lg:justify-end xl:col-span-1 xl:flex-row xl:justify-end">
                    <Link
                      href={`/admin/articles/${product.id}/edit`}
                      className="inline-flex min-h-9 items-center justify-center rounded-full border border-[#efc7d9] bg-white px-3.5 py-1.5 text-xs font-medium text-[#6f324e] transition hover:border-[#e3a9c5] hover:bg-[#fff7fb]"
                    >
                      Modifier
                    </Link>
                    <Link
                      href={`/produit/${product.slug}`}
                      className="inline-flex min-h-9 items-center justify-center rounded-full border border-[#e7dde3] bg-white px-3.5 py-1.5 text-xs font-medium text-[#4f3c45] transition hover:border-[#d9c6d0] hover:bg-[#fffafb]"
                    >
                      Voir
                    </Link>
                    <DeleteProductButton
                      productId={product.id}
                      productName={product.name}
                    />
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}